import React, { Suspense, useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import routes from "./routes";
import {
  Container,
  Box,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import Swal from "sweetalert2";
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";

const drawerWidth = 200;

function AppContent() {
  const [selectedItem, setSelectedItem] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const publicRoutes = routes.filter((route) =>
    ["Login", "Register", "Password Recovery", "Reset Password"].includes(
      route.name
    )
  );
  const protectedRoutes = routes.filter(
    (route) => !publicRoutes.some((pub) => pub.name === route.name)
  );

  useEffect(() => {
    const storedAuth = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    const storedDepartmentId = localStorage.getItem("department_id");
    const currentPath = location.pathname;

    // Define path-to-item mapping
    const pathToItemKey = {
      "/dashboard/admin": "admin-dashboard",
      "/dashboard/user": "user-dashboard",
      "/dashboard/executive": "executive-dashboard",
      "/dashboard/department-head": "department-head-dashboard",
      "/dashboard/user-management": "user-management",
      "/dashboard/reports": "reports",
      "/dashboard/analytics": "analytics",
      "/dashboard/tickets": "tickets",
      "/dashboard/locations": "locations",
      "/dashboard/department": "department",
      "/dashboard/profile": "profile",
      "/dashboard/notifications": "notifications",
      "/dashboard/department-ticket": "department-ticket",
      "/dashboard/assigned-tickets": "assigned-tickets",
    };

    // Map roles to their default dashboard routes
    const roleToDefaultRoute = {
      admin: "/dashboard/admin",
      standard: "/dashboard/user",
      executive: "/dashboard/executive",
      department_head: "/dashboard/department-head",
    };

    if (storedAuth) {
      setIsAuthenticated(true);
      setRole(storedRole);
      setDepartmentId(storedDepartmentId);

      // Set selectedItem based on current path or default to role-based dashboard
      const itemKey =
        pathToItemKey[currentPath] ||
        (storedRole ? `${storedRole}-dashboard` : "dashboard");
      setSelectedItem(itemKey);

      // Handle /dashboard redirect or invalid protected routes
      if (
        currentPath === "/dashboard" ||
        !protectedRoutes.some((route) => route.path === currentPath)
      ) {
        const defaultRoute =
          roleToDefaultRoute[storedRole] || "/dashboard/user";
        if (currentPath !== defaultRoute) {
          navigate(defaultRoute, { replace: true });
        }
      }
    } else {
      // Redirect to login if not authenticated and not on a public route
      if (!publicRoutes.some((route) => route.path === currentPath)) {
        navigate("/", { replace: true });
      }
    }
  }, [location.pathname, navigate, protectedRoutes]);

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    let path;

    switch (item) {
      case "admin-dashboard":
        path = "/dashboard/admin";
        break;
      case "user-dashboard":
        path = "/dashboard/user";
        break;
      case "executive-dashboard":
        path = "/dashboard/executive";
        break;
      case "department-head-dashboard":
        path = "/dashboard/department-head";
        break;
      case "user-management":
        path = "/dashboard/user-management";
        break;
      case "reports":
        path = "/dashboard/reports";
        break;
      case "profile":
        path = "/dashboard/profile";
        break;
      case "analytics":
        path = "/dashboard/analytics";
        break;
      case "tickets":
        path = "/dashboard/tickets";
        break;
      case "notifications":
        path = "/dashboard/notifications";
        break;
      case "locations":
        path = "/dashboard/locations";
        break;
      case "department":
        path = "/dashboard/department";
        break;
      case "department-ticket":
        path = "/dashboard/department-ticket";
        break;
      case "assigned-tickets":
        path = "/dashboard/assigned-tickets";
        break;
      default:
        path = role
          ? `/dashboard/${role === "standard" ? "user" : role}`
          : "/dashboard/user";
    }
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
    const storedRole = localStorage.getItem("role");
    const storedDepartmentId = localStorage.getItem("department_id");
    setRole(storedRole);
    setDepartmentId(storedDepartmentId);
    localStorage.setItem("isAuthenticated", "true");

    const roleToDefaultRoute = {
      admin: "/dashboard/admin",
      standard: "/dashboard/user",
      executive: "/dashboard/executive",
      department_head: "/dashboard/department-head",
    };

    const rolePath = roleToDefaultRoute[storedRole] || "/dashboard/user";
    setSelectedItem(
      storedRole
        ? `${storedRole === "standard" ? "user" : storedRole}-dashboard`
        : "dashboard"
    );
    navigate(rolePath);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setRole(null);
    setDepartmentId(null);
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("department_id");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("rememberedPassword");
    setSelectedItem("");
    Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: "Successfully logged out!",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    }).then(() => {
      navigate("/");
    });
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {isAuthenticated && (
        <Box sx={{ display: "flex", flex: 1, width: "100%" }}>
          <Sidebar
            selectedItem={selectedItem}
            onItemSelect={handleItemSelect}
            open={mobileOpen}
            onClose={handleDrawerClose}
            role={role}
            onLogout={handleLogout}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
              maxWidth: "100%",
            }}
          >
            <Header
              onMenuToggle={handleMenuToggle}
              onLogout={handleLogout}
              role={role}
              departmentId={departmentId}
            />
            <Box
              sx={{
                p: { xs: 1, sm: 2 },
                mt: { xs: 7, sm: 8 },
                flex: 1,
                overflowY: "auto",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              <Container
                maxWidth={false}
                sx={{ px: { xs: 1, sm: 2 }, maxWidth: "100%" }}
              >
                <Suspense
                  fallback={
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  <Routes>
                    {protectedRoutes.map((route, index) => (
                      <Route
                        key={index}
                        path={route.path}
                        element={<route.component />}
                      />
                    ))}
                    <Route
                      path="/dashboard"
                      element={
                        role ? (
                          <Navigate
                            to={
                              role === "standard"
                                ? "/dashboard/user"
                                : `/dashboard/${role}`
                            }
                            replace
                          />
                        ) : (
                          <Navigate to="/" replace />
                        )
                      }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </Container>
            </Box>
          </Box>
        </Box>
      )}
      {!isAuthenticated && (
        <Suspense
          fallback={
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          }
        >
          <Routes>
            {publicRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={
                  route.name === "Login" ? (
                    <route.component onLogin={handleLogin} />
                  ) : (
                    <route.component />
                  )
                }
                exact={route.exact}
              />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      )}
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
